import { Bot, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { Alert } from '@/components/ui/Alert';
import { Avatar } from '@/components/ui/Avatar';
import { Badge, StatusBadge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { EmptyState } from '@/components/ui/EmptyState';
import { Switch } from '@/components/ui/Switch';
import { ApiError, getErrorMessage } from '@/lib/api-error';
import { formatDateTime } from '@/lib/format';
import { leadStatus } from '@/theme';
import type { ConversacionResumen } from '@/types/conversations';
import { ConversationThread } from './ConversationThread';
import { MessageComposer } from './MessageComposer';
import { useConversation } from './use-conversations';
import { useConversationMessages } from './use-conversation-messages';
import { useDeleteConversation } from './use-delete-conversation';
import { useResendMessage } from './use-resend-message';
import { useSendImage } from './use-send-image';
import { useSendMessage } from './use-send-message';
import { useSetBotActive } from './use-set-bot-active';
import {
  asistenteMeta,
  botStateMeta,
  channelMeta,
  contactName,
  describeBotPausa,
  describeContactChannel,
  describeOrdenResumen,
  leadOriginLabel,
} from './inbox-format';

/**
 * Columna derecha del inbox: la conversación abierta.
 *
 * Es la pantalla donde se decide el handoff **y donde se responde a mano**. Ahí está lo único que
 * el panel no puede deducir de un vistazo en la bandeja: el historial completo (con los bloques JSON
 * de pedido colapsados, que es como el cliente manda los datos), el interruptor del bot y el
 * composer.
 *
 * **Handoff** (`PATCH .../conversations/:id/bot`, solo ADMIN): apagado el bot, el mensaje del
 * cliente NO se auto-responde: la conversación queda esperando a una persona y la bandeja la marca
 * como "requiere atención" (`requiereAtencionHumana` = `botActivo === false`). Por eso el
 * interruptor es una decisión de negocio y no un ajuste: se explica qué pasa en cada estado
 * (`botStateMeta`) y quien no es ADMIN lo ve pero no lo toca (el backend responde 403).
 *
 * **Respuesta manual** (`POST .../messages` para texto y `POST .../fotos` para una imagen, cualquier
 * miembro): el composer va DEBAJO del hilo porque el chat se lee de arriba abajo y lo último es lo que
 * se acaba de escribir. El panel no decide nada del envío: se lo deja a `useSendMessage` /
 * `useSendImage` (ver sus cabeceras para el 502, la ventana de 24 h y el `PUBLIC_BASE_URL` que exige
 * la foto).
 *
 * Los datos los trae el propio panel (detalle + historial + mutaciones) y la selección se la pasa
 * `InboxPage`: así cambiar de conversación es cambiar un `id`, y el panel se encarga de todo lo que
 * cuelga de él.
 */

export interface ConversationPanelProps {
  tenantId: string | null;
  /** Conversación abierta (`null` = ninguna: se muestra el estado vacío). */
  conversationId: string | null;
  /** La fila de la bandeja, para pintar la cabecera al instante (el detalle tarda una petición). */
  resumen: ConversacionResumen | null;
  /** ¿Puede hacer handoff? (`role === 'ADMIN'`). */
  canEdit: boolean;
  /** Se avisó que el chat ya no existe: la página cierra el panel y vuelve a la bandeja. */
  onDeleted?: () => void;
}

export function ConversationPanel({
  tenantId,
  conversationId,
  resumen,
  canEdit,
  onDeleted,
}: ConversationPanelProps) {
  // El detalle es la MISMA tarjeta del inbox + las últimas órdenes del contacto, así que mientras
  // carga se usa el resumen del listado y la cabecera no parpadea.
  const detalleQuery = useConversation(tenantId, conversationId);
  const mensajesQuery = useConversationMessages(tenantId, conversationId);
  const setBot = useSetBotActive(tenantId);
  const borrar = useDeleteConversation(tenantId);
  const enviar = useSendMessage(tenantId, conversationId);
  const enviarFoto = useSendImage(tenantId, conversationId);
  const reenviar = useResendMessage(tenantId, conversationId);

  // Borrado del chat: el diálogo vive en este panel (no hace falta estado en la página) y solo se
  // puede abrir si el usuario es ADMIN, que es lo que exige el backend.
  const [confirmarBorrado, setConfirmarBorrado] = useState(false);
  /**
   * El chat ya tenía ventas y el backend lo negó con 409: el diálogo se queda abierto y pasa a
   * preguntar de verdad («¿la borramos igual?»), que es la segunda confirmación del flag
   * `permitirConOrdenes`. Es estado aparte porque el primer intento ya falló.
   */
  const [borrarConVentas, setBorrarConVentas] = useState(false);

  /** El 409 con ventas es el único caso en que se reintenta: se marca y se reintenta con el flag. */
  function confirmarBorradoChat() {
    borrar.mutate(
      { conversationId: conversationId as string, permitirConOrdenes: borrarConVentas },
      {
        onSuccess: () => {
          setConfirmarBorrado(false);
          setBorrarConVentas(false);
          onDeleted?.();
        },
        onError: (error) => {
          // El único 409 de este endpoint es «el chat tiene ventas»: el diálogo cambia a la
          // segunda confirmación y el usuario aprieta otra vez.
          if (error instanceof ApiError && error.isConflict) setBorrarConVentas(true);
        },
      },
    );
  }

  const conversacion: ConversacionResumen | null = detalleQuery.data ?? resumen;

  if (!conversationId || !conversacion) {
    return (
      <Card className="flex h-full items-center justify-center p-6">
        <EmptyState
          icon={<Bot size={22} />}
          title="Elige una conversación"
          description="Abre una conversación de la bandeja para ver el historial completo, los pedidos del contacto y el estado del bot, responder a mano y pausarlo o reactivarlo si hay que atender."
        />
      </Card>
    );
  }

  const nombre = contactName(conversacion);
  const canal = channelMeta[conversacion.channel];
  const CanalIcon = canal.icon;
  const lead = leadStatus[conversacion.estadoLead];
  const asistente = asistenteMeta[conversacion.asistenteActivo];
  const bot = botStateMeta(conversacion.botActivo);
  /** Frase que explica la pausa del bot (o `null` si está encendido o no hay motivo guardado). */
  const pausaExplicada = describeBotPausa(conversacion);
  const ordenes = detalleQuery.data?.ordenes ?? [];

  return (
    <Card className="flex h-full min-h-0 flex-col overflow-hidden">
      <CardHeader
        actions={
          canEdit ? (
            <div className="flex items-center gap-3">
              <Switch
                checked={conversacion.botActivo}
                disabled={setBot.isPending}
                label={`Bot activo en la conversación de ${nombre}`}
                onChange={(checked) => setBot.mutate({ conversationId, botActivo: checked })}
              />

              {/* Limpiar la memoria del bot de este chat: se va con todos sus mensajes. */}
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  setBorrarConVentas(false);
                  borrar.reset();
                  setConfirmarBorrado(true);
                }}
              >
                <Trash2 size={16} aria-hidden="true" />
                Borrar conversación
              </Button>
            </div>
          ) : (
            <Badge tone={bot.tone} icon={<Bot size={12} />}>
              {bot.label}
            </Badge>
          )
        }
      >
        <div className="flex min-w-0 items-center gap-3">
          <Avatar name={nombre} size="lg" />

          <div className="min-w-0">
            <CardTitle className="truncate">{nombre}</CardTitle>
            <CardDescription>{describeContactChannel(conversacion)}</CardDescription>
          </div>
        </div>
      </CardHeader>

      <div className="mt-3 flex flex-wrap items-center gap-1.5 px-6">
        <Badge tone={canal.tone} icon={<CanalIcon size={12} />}>
          {canal.label}
        </Badge>

        <StatusBadge label={lead.label} tone={lead.tone} />
        <Badge tone="neutral">{`Estado ${leadOriginLabel[conversacion.estadoLeadOrigen].toLowerCase()}`}</Badge>
        <Badge tone={asistente.tone}>Asistente: {asistente.label}</Badge>

        {conversacion.requiereAtencionHumana ? (
          <Badge tone="warning">Requiere atención humana</Badge>
        ) : null}

        {/* De qué anuncio/campaña salió el contacto: es lo que permite atribuir la venta. */}
        {conversacion.adId ? (
          <Badge tone="info">
            {conversacion.adHeadline ? `Anuncio: ${conversacion.adHeadline}` : `Anuncio: ${conversacion.adId}`}
          </Badge>
        ) : null}
        {conversacion.campaignId ? (
          <Badge tone="brand">Campaña: {conversacion.campaignId}</Badge>
        ) : null}
      </div>

      <div className="mt-3 space-y-2 px-6 pb-3">
        {setBot.isError ? <Alert>{getErrorMessage(setBot.error)}</Alert> : null}
        {reenviar.isError ? <Alert>{getErrorMessage(reenviar.error)}</Alert> : null}

        <div className="rounded-panel border border-line bg-surface-muted px-3 py-2">
          <p className="flex items-center gap-2 text-body font-semibold text-ink">
            <Bot size={16} aria-hidden="true" />
            {bot.label}
          </p>
          <p className="mt-1 text-caption text-ink-muted">{bot.description}</p>
        </div>

        {/* Por qué está apagado el bot: sin esto la pantalla solo mostraba un interruptor apagado y el
            motivo (handoff del cliente, pedido que necesita una persona, fallo de credenciales de
            DeepSeek) vivía únicamente en el log del servidor. */}
        {pausaExplicada ? (
          <Alert tone="warning">
            {pausaExplicada}
            {conversacion.botPausadoAt ? ` (${formatDateTime(conversacion.botPausadoAt)})` : ''}
          </Alert>
        ) : null}

        {canEdit ? null : (
          <p className="text-micro text-ink-muted">
            Tu rol en este negocio es de solo lectura: pausar o reanudar el bot lo puede hacer un
            administrador.
          </p>
        )}

        {detalleQuery.isError ? <Alert>{getErrorMessage(detalleQuery.error)}</Alert> : null}

        {/* Los pedidos van colapsados: son contexto, no el motivo por el que se abre el inbox. */}
        {ordenes.length > 0 ? (
          <details className="rounded-panel border border-line px-4 py-3">
            <summary className="cursor-pointer text-caption font-semibold text-ink-soft">
              Últimos pedidos del contacto ({ordenes.length})
            </summary>

            <ul className="mt-2 space-y-2">
              {ordenes.map((orden) => (
                <li
                  key={orden.id}
                  className="flex flex-wrap items-baseline justify-between gap-2 text-caption"
                >
                  <span className="text-ink-muted">{formatDateTime(orden.createdAt)}</span>
                  <span className="font-semibold text-ink">{describeOrdenResumen(orden)}</span>
                </li>
              ))}
            </ul>
          </details>
        ) : null}
      </div>

      <ConversationThread
        contacto={nombre}
        total={mensajesQuery.total}
        mensajes={mensajesQuery.mensajes}
        isPending={mensajesQuery.isPending}
        error={mensajesQuery.error}
        hasMore={mensajesQuery.hasNextPage}
        loadingMore={mensajesQuery.isFetchingNextPage}
        onLoadMore={() => void mensajesQuery.fetchNextPage()}
        onRefresh={() => void mensajesQuery.refetch()}
        refreshing={mensajesQuery.isFetching}
        onResend={(mensaje) =>
          reenviar.mutate({ conversationId: conversationId as string, messageId: mensaje.id })
        }
        resendingId={reenviar.isPending ? (reenviar.variables?.messageId ?? null) : null}
      />

      {/* Responder a mano: va debajo del hilo (el chat se lee de arriba abajo) y lo pueden usar los
          dos roles, porque atender la conversación es justo lo que hace el AGENTE_HUMANO. Las dos
          mutaciones (texto y foto) se resetean entre sí para que el aviso que se vea sea SIEMPRE el
          del último intento: si no, un 502 de la foto se quedaría pegado encima de un texto que sí
          salió. */}
      <MessageComposer
        contacto={nombre}
        sending={enviar.isPending || enviarFoto.isPending}
        error={enviar.error ?? enviarFoto.error}
        onSend={async (contenido) => {
          enviarFoto.reset();
          return enviar.mutateAsync({ conversationId: conversationId as string, contenido });
        }}
        onSendImage={async (imagen) => {
          enviar.reset();
          return enviarFoto.mutateAsync({ conversationId: conversationId as string, imagen });
        }}
      />

      {/* Borrado del chat. El diálogo cambia de texto según si el chat ya tenía ventas: el primer
          intento es el normal y, si el backend responde 409, el mismo diálogo pasa a pedir la
          segunda confirmación (esa es la que manda `permitirConOrdenes`). El error del backend se
          muestra tal cual (`getErrorMessage`), que es donde está el «tiene N órdenes». */}
      <ConfirmDialog
        open={confirmarBorrado}
        onClose={() => setConfirmarBorrado(false)}
        onConfirm={confirmarBorradoChat}
        title={
          borrarConVentas
            ? 'Esta conversación tiene pedidos: ¿la borramos igual?'
            : 'Borrar esta conversación'
        }
        description={
          borrarConVentas ? (
            <>
              <p>
                Los <strong>pedidos no se borran</strong>: quedan guardados, pero sin este chat. Vas a
                perder el historial de la conversación y el bot dejará de acordarse de lo que se
                hablamos acá.
              </p>
              <p className="mt-2">
                Si solo querías limpiar la conversación y no perder esos pedidos, cancelá y preguntale
                al cliente por otro medio.
              </p>
            </>
          ) : (
            <>
              Se borra el chat <strong>y todos sus mensajes</strong>. El bot deja de acordarse de lo
              que hablamos acá: la próxima vez que escriba este contacto se abre una conversación
              nueva y empieza de cero. No se puede deshacer.
            </>
          )
        }
        confirmLabel={borrarConVentas ? 'Borrar igual' : 'Borrar conversación'}
        loading={borrar.isPending}
        error={borrar.error ? getErrorMessage(borrar.error) : null}
      />
    </Card>
  );
}
